import { useCallback, useEffect } from 'react';
import { useMobileDevice } from '../../global/useMobile';
import { CustomWindow } from '../../interfaces/OneSignalInterface';
import { GoogleCredentialResponse, ElectronTokens } from '../../interfaces/GoogleInterface';
import { useHistory, useLocation } from 'react-router-dom';
import { isElectronApp } from '../../helpers/common';
import { REACT_APP_GOOGLE_CLIENT_ID } from '../../global/environment';
import { googleLogin } from '../../services/authServices';
import { APP_INIT_RESPONSE_TYPE, BUTTON_TYPE } from '../../global/constants';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { captureException } from '../../services/logService';
import { GoogleButtonWrapper, HiddenGoogleButton } from './styles';
import Button from '../button';
import { ROUTES } from '../../routes/urls';

declare const window: CustomWindow;

interface Props {
  loading: boolean;
  setLoading: (value: boolean) => void;
}

const GoogleButton = ({ setLoading, loading }: Props) => {
  const isMobile = useMobileDevice();
  //Other variable
  let history = useHistory();
  const locationSearch = useLocation().search;

  // Initialize Google Identity Services
  useEffect(() => {
    if (isElectronApp()) return; // Do not use GIS inside Electron

    const initGoogle = () => {
      try {
        if (!window.google || !window.google.accounts || !window.google.accounts.id) return;
        window.google.accounts.id.initialize({
          client_id: REACT_APP_GOOGLE_CLIENT_ID || '',
          callback: async (response: GoogleCredentialResponse) => {
            const idToken = response?.credential;
            if (idToken) {
              try {
                setLoading(true);
                const result = await googleLogin(idToken);
                if (result && result.type === APP_INIT_RESPONSE_TYPE.REDIRECT && result.path) {
                  window.OneSignal?.push(function () {
                    window.OneSignal?.setSubscription(true);
                  });
                  history.push(result.path);
                } else {
                  let organization = UserPreferenceSingleton.getInstance().getWorkspace();
                  if (organization?.id) {
                    const intercomPayload = {
                      company: {
                        name: organization?.name,
                        id: organization?.id
                      }
                    };
                    if (window && window.posthog) {
                      window.posthog.group('Workspace', intercomPayload.company?.id, {
                        name: intercomPayload.company?.name
                      });
                    }
                    const returnUrl = new URLSearchParams(locationSearch).get('return-url');
                    if (returnUrl) history.push(decodeURIComponent(returnUrl));
                    else history.push(ROUTES.INBOX);
                  }
                }
              } catch (error) {
                captureException(error);
              } finally {
                setLoading(false);
              }
            }
          }
        });
        // Render Google's official button (hidden) so we can trigger it from our custom button
        setTimeout(() => {
          const container = document.getElementById('google-signin-btn');
          if (container && window.google?.accounts?.id) {
            window.google.accounts.id.renderButton(container, {
              theme: 'outline',
              size: 'large',
              width: 320,
              text: 'signin_with'
            });
          }
        }, 0);
      } catch (e) {
        captureException(e);
      }
    };

    // If script already loaded
    if (window.google && window.google.accounts && window.google.accounts.id) {
      initGoogle();
      return;
    }

    // Wait for script load
    const onLoad = () => initGoogle();
    window.addEventListener('load', onLoad);
    return () => window.removeEventListener('load', onLoad);
  }, [history, locationSearch, setLoading]);

  const onGoogleClick = useCallback(() => {
    try {
      if (isElectronApp()) {
        // Ask main process to start native OAuth (opens system browser)
        window.electron?.startGoogleOAuth?.();
        return;
      }
      // Browser: click the GIS button
      const container = document.getElementById('google-signin-btn');
      const googleBtn = container?.querySelector('[role="button"]') as HTMLElement | null;
      googleBtn?.click();
    } catch (e) {
      captureException(e);
    }
  }, []);

  useEffect(() => {
    if (!window.electron?.onGoogleOAuthComplete) return;
    window.electron.onGoogleOAuthComplete(async (tokens: ElectronTokens) => {
      try {
        setLoading(true);
        // Prefer id_token if you need a JWT; else send access_token to backend
        const idToken = tokens?.id_token;
        if (idToken) {
          const result = await googleLogin(idToken);
          if (result && result.type === APP_INIT_RESPONSE_TYPE.REDIRECT && result.path) {
            window.OneSignal?.push(function () {
              window.OneSignal?.setSubscription(true);
            });
            history.push(result.path);
          } else {
            let organization = UserPreferenceSingleton.getInstance().getWorkspace();
            if (organization?.id) {
              const intercomPayload = {
                company: {
                  name: organization?.name,
                  id: organization?.id
                }
              };
              if (window && window.posthog) {
                window.posthog.group('Workspace', intercomPayload.company?.id, {
                  name: intercomPayload.company?.name
                });
              }
              const returnUrl = new URLSearchParams(locationSearch).get('return-url');
              if (returnUrl) history.push(decodeURIComponent(returnUrl));
              else history.push(ROUTES.INBOX);
            }
          }
        }
      } finally {
        setLoading(false);
      }
    });
  }, [history, locationSearch, setLoading]);

  return (
    <GoogleButtonWrapper>
      <Button
        variant={BUTTON_TYPE.SECONDARY}
        buttonType='button'
        onClick={onGoogleClick}
        title='Continue with Google'
        width='100%'
        iconPosition='start'
        size={isMobile ? 'large' : 'medium'}
        imageUrl={'google_logo'}
        disabled={loading}
        // isLoading={loading}
      />
      {!isElectronApp() && (
        <HiddenGoogleButton>
          <div id='google-signin-btn' aria-label={'Continue with Google'} />
        </HiddenGoogleButton>
      )}
    </GoogleButtonWrapper>
  );
};

export default GoogleButton;
